/**
 * SKU explosion — restating a grid's rows one level DOWN the product relation graph.
 *
 * A parent (a combo, a customisation variant, a clearance listing) is replaced by the base
 * SKUs it is made of: each parent's figures are multiplied by the edge quantity and added
 * into the child's row, and the parent row disappears so nothing is counted twice.
 *
 * This is deliberately the easy direction. Rolling UP would have to decide which parent
 * claims a component that feeds several — basil has one component sitting in 136 combos —
 * and that is an allocation policy, not a transform. Exploding down, every parent pushes its
 * own quantity outward independently and nothing competes.
 *
 * ── The one rule that makes it correct ──────────────────────────────────────────────
 * Columns are either EXTENSIVE (a quantity: units, units/day) or INTENSIVE (a ratio: days
 * of cover, a percentage, a per-unit price). Extensive figures scale with the edge quantity
 * and add. Intensive figures do neither — they are recomputed from the extensive figures
 * once every addition has landed.
 *
 * Adding a ratio is the mistake this module exists to prevent. A base SKU at 12.6 days of
 * cover absorbing a clearance pool at 3,800 days does not end up at 3,812 days; it ends up
 * at 16.3, because the true figure is (391 + 114) / (30.93 + 0.03). Hence the two-callback
 * shape below: `addScaled` moves the extensive fields, `recompute` re-derives everything
 * else, and it runs strictly after all the adds.
 *
 * The caller supplies both because only the caller knows which of its fields are which —
 * and because a grid's extensive fields are often nested (`row.inbound.inTransitPo`), which
 * a flat list of column keys could not reach.
 */

/** One parent → child edge. Mirrors `ProductRelationEdgeDto` on the backend. */
export interface ProductRelationEdge {
    parentId: string;
    childId: string;
    quantity: number;
    childName: string | null;
    childSku: string | null;
    childCategory: string | null;
    childProductGroup: string | null;
    childTier: string | null;
}

/**
 * What an exploded row absorbed, attached to the row so the UI can explain itself.
 *
 * Kept as a separate field rather than folded into the row type so that a grid which has
 * not been taught about explosion still type-checks and still renders — it simply ignores
 * a property it does not read.
 */
export interface ExplosionOrigin {
    /** Parents that contributed to this row, with the multiplier each contributed at. */
    contributors: Array<{ parentId: string; quantity: number }>;
    /**
     * True when the row did not exist before the explosion and was synthesised from the
     * edge's child metadata. Its non-additive fields (supplier, sourcing model) are unknown.
     */
    synthetic: boolean;
}

/** Rows carry their origin under this key once exploded. */
export const EXPLOSION_ORIGIN_KEY = '__explosion' as const;

export type Exploded<T> = T & { [EXPLOSION_ORIGIN_KEY]?: ExplosionOrigin };

export interface ExplodeOptions<T> {
    /** The rows as the grid would show them un-exploded. Never mutated. */
    rows: T[];
    /** The merchant's full relation graph. A partial graph silently loses units. */
    edges: ProductRelationEdge[];
    /** The product id a row is keyed on. */
    getId: (row: T) => string;
    /**
     * Deep-copy a row far enough that `addScaled` cannot write through to the caller's data.
     * A shallow `{...row}` is NOT enough when extensive fields are nested.
     */
    cloneRow: (row: T) => T;
    /**
     * `target += quantity × source`, for EXTENSIVE fields only. Ratios must be left alone
     * here — `recompute` owns them.
     */
    addScaled: (target: T, source: T, quantity: number) => void;
    /** Re-derive every INTENSIVE field from the row's own extensive fields. */
    recompute: (row: T) => void;
    /**
     * Build a zeroed row for a child that had no row of its own — filtered out of the
     * caller's slice, or simply never stocked. Return null to drop the contribution
     * instead, which loses units and should be a deliberate choice.
     */
    makeEmptyRow: (edge: ProductRelationEdge) => T | null;
}

/**
 * Rows restated one level down the graph.
 *
 * Order is preserved for rows that survive untouched, with synthesised children appended —
 * a grid that sorts its own rows will re-sort anyway, and a grid that does not should not
 * see its list reshuffled just because the toggle was flipped.
 */
export function explodeRows<T>(options: ExplodeOptions<T>): Array<Exploded<T>> {
    const { rows, edges, getId, cloneRow, addScaled, recompute, makeEmptyRow } = options;

    const edgesByParent = new Map<string, ProductRelationEdge[]>();
    for (const edge of edges) {
        const list = edgesByParent.get(edge.parentId);
        if (list) list.push(edge);
        else edgesByParent.set(edge.parentId, [edge]);
    }

    // Nothing to do — and an important short-circuit, because this runs on every filter
    // change on grids where no row is a parent at all.
    if (edgesByParent.size === 0) return rows as Array<Exploded<T>>;

    const survivors: Array<Exploded<T>> = [];
    const byId = new Map<string, Exploded<T>>();
    /** Parents, held aside until every survivor exists so contributions land in one pass. */
    const parents: T[] = [];

    for (const row of rows) {
        if (edgesByParent.has(getId(row))) {
            parents.push(row);
            continue;
        }
        const copy = cloneRow(row) as Exploded<T>;
        survivors.push(copy);
        byId.set(getId(copy), copy);
    }

    // Appended after the survivors, and tracked separately so a child synthesised for one
    // parent is reused by the next rather than duplicated.
    const synthesised: Array<Exploded<T>> = [];

    for (const parent of parents) {
        for (const edge of edgesByParent.get(getId(parent)) ?? []) {
            // A zero or negative multiplier would silently delete the parent's figures.
            if (!(edge.quantity > 0)) continue;

            let target = byId.get(edge.childId);
            if (!target) {
                const created = makeEmptyRow(edge);
                if (!created) continue;
                target = created as Exploded<T>;
                target[EXPLOSION_ORIGIN_KEY] = { contributors: [], synthetic: true };
                byId.set(edge.childId, target);
                synthesised.push(target);
            }

            addScaled(target, parent, edge.quantity);

            const origin = target[EXPLOSION_ORIGIN_KEY] ?? {
                contributors: [],
                synthetic: false,
            };
            origin.contributors.push({
                parentId: getId(parent),
                quantity: edge.quantity,
            });
            target[EXPLOSION_ORIGIN_KEY] = origin;
        }
    }

    const result = [...survivors, ...synthesised];
    // Strictly after every addition. Recomputing inside the loop above would derive a ratio
    // from a half-summed denominator, and the error would depend on edge iteration order.
    for (const row of result) recompute(row);

    return result;
}

/**
 * The ids whose inbound detail a row's drill-in has to fetch: its own, plus every parent
 * that contributed to it.
 *
 * The drill-in calls a per-product endpoint, so an exploded row that absorbed a parent's
 * in-transit units would otherwise open a panel that disagrees with the cell above it. In
 * practice the list is one id — across all of basil only a single draft PO sits on a parent
 * SKU — but the cost of being right is one extra request on the rows that need it.
 */
export function explosionSourceIds<T>(row: Exploded<T>, ownId: string): string[] {
    const contributors = row[EXPLOSION_ORIGIN_KEY]?.contributors ?? [];
    if (contributors.length === 0) return [ownId];
    return [...new Set([ownId, ...contributors.map((c) => c.parentId)])];
}

/** Whether this row absorbed anything — drives the "⊕" affordance and its tooltip. */
export function isExplodedInto<T>(row: Exploded<T>): boolean {
    return (row[EXPLOSION_ORIGIN_KEY]?.contributors.length ?? 0) > 0;
}

/**
 * What was folded into this row, for the cell that has to explain itself.
 *
 * An accessor rather than a bare property read so grids do not have to widen their own row
 * types with a field they never set — the origin is attached by the explosion, not by the
 * grid, and a `HealthRow` should not have to declare it to be able to display it.
 */
export function explosionContributors<T>(
    row: Exploded<T>,
): ReadonlyArray<{ parentId: string; quantity: number }> {
    return row[EXPLOSION_ORIGIN_KEY]?.contributors ?? [];
}
